import React from "react";
import LegalPage from "./LegalPage";

function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      description="How Klean Leaf collects, uses and protects information from website visitors and private label inquiries."
      path="/privacy-policy"
    >
      <p>
        This Privacy Policy explains how Klean Leaf LLC ("Klean Leaf," "we," "us")
        collects, uses and shares information when you visit{" "}
        <a href="https://www.kleanleaf.us">www.kleanleaf.us</a> (the "Site") or contact
        us about private label manufacturing. Klean Leaf is a business-to-business
        manufacturer of laundry detergent sheets and household cleaning products;
        our Site is intended for brands, retailers, wholesalers, distributors and
        other businesses.
      </p>

      <section aria-labelledby="privacy-section-1">
        <h2 id="privacy-section-1">1. Who We Are</h2>
        <p>
          Klean Leaf LLC is based in Columbus, Ohio, United States. Our U.S. team
          handles client communication, quotations, samples and sales, and our
          manufacturing operations are based in China. You can reach us at{" "}
          <a href="mailto:hello@kleanleaf.us">hello@kleanleaf.us</a>.
        </p>
      </section>

      <section aria-labelledby="privacy-section-2">
        <h2 id="privacy-section-2">2. Information We Collect</h2>
        <p>
          <strong>Information you give us.</strong> When you submit an inquiry,
          request samples or a quote, or contact us by email or phone, we collect:
        </p>
        <ul>
          <li>Your name, email address, company name and phone number, if provided;</li>
          <li>
            Project details, such as products of interest, project requirements,
            estimated order quantities, packaging preferences, expected launch
            date, requested assistance and your message;
          </li>
          <li>Shipping information and contact details when needed for samples or orders; and</li>
          <li>
            Any other information you choose to share with us, such as artwork,
            specifications or documents.
          </li>
        </ul>
        <p>
          We may also collect business contact details you share with us at trade
          shows or in other business meetings.
        </p>
        <p>
          <strong>Information collected automatically.</strong> We use Google
          Analytics to understand how visitors use the Site. Google Analytics uses
          cookies and similar technologies to collect information such as the pages
          you visit, website interactions, how you arrived at the Site, your browser
          and device type, your approximate location derived from your IP address,
          and cookie or similar identifiers.
        </p>
        <p>
          We do not knowingly collect sensitive personal information, and we ask
          that you do not send it to us.
        </p>
      </section>

      <section aria-labelledby="privacy-section-3">
        <h2 id="privacy-section-3">3. How We Use Information</h2>
        <p>We use the information described above for legitimate business and operational purposes, including to:</p>
        <ul>
          <li>Respond to inquiries and provide quotes, samples, product information and project updates;</li>
          <li>Develop samples and process, manufacture, pack and fulfill customer orders;</li>
          <li>Coordinate customer projects with manufacturing, quality control and logistics teams;</li>
          <li>Manage our business relationship with you, including contracts, invoicing and recordkeeping;</li>
          <li>Understand aggregate website traffic and improve the Site's functionality and our services;</li>
          <li>
            Send you legitimate business communications about our products, if you
            have contacted us or agreed to receive them, as permitted by applicable
            law (you can opt out at any time); and
          </li>
          <li>Protect our rights, prevent fraud or spam, and comply with legal obligations.</li>
        </ul>
      </section>

      <section aria-labelledby="privacy-section-4">
        <h2 id="privacy-section-4">4. Legal Bases (EEA and UK Visitors)</h2>
        <p>
          If you are in the European Economic Area or the United Kingdom, our legal
          bases for processing your information include taking steps at your request
          before entering into a contract and performing a contract with you; our
          legitimate interests in running and improving our business and responding
          to business inquiries; and compliance with legal obligations.
        </p>
        <p>
          Where processing requires your consent, consent is the applicable legal
          basis. Analytics cookies require prior consent where applicable law
          requires it. Section 6 describes the Site's current analytics implementation
          and the browser controls available to you.
        </p>
      </section>

      <section aria-labelledby="privacy-section-5">
        <h2 id="privacy-section-5">5. How We Share Information</h2>
        <p>
          We do not sell personal information, and we do not share it for
          cross-context behavioral advertising. We share information only when
          reasonably necessary to operate our business, serve customer projects,
          use service providers or comply with applicable laws, as follows:
        </p>
        <ul>
          <li>
            <strong>Our team and manufacturing operations.</strong> Project
            information is shared with our production, quality control and logistics
            teams, including teams in China, so we can prepare quotes, samples and orders.
          </li>
          <li>
            <strong>Service providers that act on our behalf,</strong> including
            Google (website analytics and fonts), Web3Forms (delivery of website
            form submissions), our email service provider (business communications),
            and shipping and logistics providers (samples and orders).
          </li>
          <li>
            <strong>Legal and safety reasons,</strong> when required by law or to
            protect our rights, our clients or others.
          </li>
          <li>
            <strong>Business transfers,</strong> in connection with a merger,
            acquisition, financing or sale of all or part of our business.
          </li>
          <li><strong>With your direction or consent.</strong></li>
        </ul>
        <p>
          Providing information to these operational service providers to support
          our business and customer projects is not a sale of customer data. We
          treat non-public customer project information with appropriate confidentiality
          and use it to evaluate and serve those projects.
        </p>
      </section>

      <section aria-labelledby="privacy-section-6">
        <h2 id="privacy-section-6">6. Cookies and Analytics</h2>
        <p>
          The Site uses Google Analytics cookies (such as <code>_ga</code> and{" "}
          <code>_ga_&lt;ID&gt;</code>) to measure Site use. These cookies contain
          identifiers used to distinguish visitors and sessions; they do not identify
          you by name. Google Analytics currently loads when you visit
          the Site; the Site does not currently offer a cookie consent or preference
          interface.
        </p>
        <p>
          You can block or delete cookies in your browser settings, or install the{" "}
          <a href="https://tools.google.com/dlpage/gaoptout">Google Analytics Opt-out Browser Add-on</a>.
          To learn how Google uses this data, see{" "}
          <a href="https://policies.google.com/technologies/partner-sites">Google's information about partner sites</a>.
        </p>
        <p>
          The Site also uses session storage in your browser to remember page scroll
          positions during navigation.
        </p>
      </section>

      <section aria-labelledby="privacy-section-7">
        <h2 id="privacy-section-7">7. Do Not Track</h2>
        <p>
          Some browsers send "Do Not Track" signals. Because there is no common
          industry standard for these signals, the Site does not currently respond
          to them. For information about Google Analytics and the choices available
          to you, see Section 6 above.
        </p>
      </section>

      <section aria-labelledby="privacy-section-8">
        <h2 id="privacy-section-8">8. International Data Transfers</h2>
        <p>
          We are based in the United States and manufacture in China, and our service
          providers may process information in other countries. Your information may
          therefore be transferred to and processed in the United States, China and
          other countries whose data protection laws may differ from those in your country.
        </p>
        <p>
          To request information about the international transfers relevant to your
          project, contact us at <a href="mailto:hello@kleanleaf.us">hello@kleanleaf.us</a>.
        </p>
      </section>

      <section aria-labelledby="privacy-section-9">
        <h2 id="privacy-section-9">9. How Long We Keep Information</h2>
        <p>
          We keep inquiry and client records for as long as reasonably needed to
          respond to you, manage our business relationship and fulfill the purposes
          described in this Policy, including applicable legal, tax and accounting
          obligations. The appropriate period depends on the nature of the information,
          the status of your inquiry or project, and those obligations.
        </p>
        <p>
          Google Analytics user- and event-level data is subject to the retention
          settings configured in our Google Analytics property. You may contact us
          for information about retention or to request deletion as described below.
        </p>
      </section>

      <section aria-labelledby="privacy-section-10">
        <h2 id="privacy-section-10">10. Security</h2>
        <p>
          We use reasonable administrative, technical, organizational and physical
          measures to protect information. No method of transmission over the
          internet or electronic storage is completely secure, so we cannot
          guarantee absolute security.
        </p>
      </section>

      <section aria-labelledby="privacy-section-11">
        <h2 id="privacy-section-11">11. Your Choices and Rights</h2>
        <p>
          You may ask us to access, correct or delete the personal information we
          hold about you, and you may opt out of business emails at any time by
          using an unsubscribe link, where provided, or contacting us.
        </p>
        <p>
          If you are in the EEA or UK, you also have the right to object to or
          restrict certain processing, to data portability, and to withdraw consent
          at any time. You may also lodge a complaint with your local data protection
          authority.
        </p>
        <p>
          To make a request, email <a href="mailto:hello@kleanleaf.us">hello@kleanleaf.us</a>.
          We will respond within the time required by applicable law and may need to
          verify your identity first.
        </p>
        <p>
          <strong>California residents.</strong> We do not sell personal information
          or share it for cross-context behavioral advertising. You may contact us
          at any time to review or request changes to the personal information you
          have submitted through the Site.
        </p>
      </section>

      <section aria-labelledby="privacy-section-12">
        <h2 id="privacy-section-12">12. Children's Privacy</h2>
        <p>
          The Site is intended for businesses and is not directed to children under
          16. We do not knowingly collect personal information from children. If you
          believe a child has provided us with personal information, please contact
          us and we will delete it.
        </p>
      </section>

      <section aria-labelledby="privacy-section-13">
        <h2 id="privacy-section-13">13. Third-Party Links</h2>
        <p>
          The Site links to third-party websites and social media pages, such as
          Instagram and Facebook. Their privacy practices are governed by their own
          policies, not this one.
        </p>
      </section>

      <section aria-labelledby="privacy-section-14">
        <h2 id="privacy-section-14">14. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will
          post the updated version on this page and change the "Last updated" date
          above. If the changes are significant, we will provide a more prominent
          notice on the Site.
        </p>
      </section>

      <section aria-labelledby="privacy-section-15">
        <h2 id="privacy-section-15">15. Contact Us</h2>
        <p>Questions or requests about this Privacy Policy:</p>
        <address>
          Klean Leaf LLC<br />
          Columbus, Ohio, United States<br />
          Email: <a href="mailto:hello@kleanleaf.us">hello@kleanleaf.us</a>
        </address>
      </section>
    </LegalPage>
  );
}

export default PrivacyPolicy;
